import { publishFeedback } from "./feedback";
export * from "./feedback";
import type { ApiErrorBody, AuthenticatedUser } from "@taunggyi-plus/domain";

export interface HealthResponse {
  readonly status: "ok";
  readonly service: "taunggyi-plus-api";
  readonly timestamp: string;
}

export interface PlatformUser {
  readonly id: string;
  readonly email?: string;
  readonly phone?: string;
  readonly user_metadata: Record<string, unknown>;
}

export interface PlatformSession {
  readonly access_token: string;
  readonly expires_at: number;
  readonly user: PlatformUser;
}

export interface DataError {
  readonly message: string;
}

export interface DataResult<T = unknown> {
  readonly data: T | null;
  readonly error: DataError | null;
  readonly count?: number | null;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiErrorBody,
  ) {
    super(body.error.message);
  }
}

interface Filter {
  readonly operator: "eq" | "in" | "is";
  readonly column: string;
  readonly value: unknown;
}

interface QueryPayload {
  table: string;
  operation: "select" | "insert" | "update" | "delete";
  columns?: string;
  values?: unknown;
  filters: Filter[];
  order?: { column: string; ascending: boolean };
  limit?: number;
  single?: boolean;
  count?: "exact";
  head?: boolean;
}

// The compatibility query builder intentionally remains dynamically typed while
// feature packages migrate from dynamically shaped database rows to shared models.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DynamicData = any;

export class DataQueryBuilder implements PromiseLike<DataResult<DynamicData>> {
  private readonly payload: QueryPayload;

  constructor(
    table: string,
    private readonly executeRequest: (payload: QueryPayload) => Promise<DataResult<DynamicData>>,
  ) {
    this.payload = { table, operation: "select", columns: "*", filters: [] };
  }

  select(columns = "*", options?: { count?: "exact"; head?: boolean }) {
    this.payload.columns = columns;
    this.payload.count = options?.count;
    this.payload.head = options?.head;
    return this;
  }

  insert(values: unknown) {
    this.payload.operation = "insert";
    this.payload.values = values;
    return this;
  }

  update(values: unknown) {
    this.payload.operation = "update";
    this.payload.values = values;
    return this;
  }

  delete() {
    this.payload.operation = "delete";
    delete this.payload.values;
    return this;
  }

  eq(column: string, value: unknown) {
    this.payload.filters.push({ operator: "eq", column, value });
    return this;
  }

  in(column: string, value: readonly unknown[]) {
    this.payload.filters.push({ operator: "in", column, value });
    return this;
  }

  is(column: string, value: null | boolean) {
    this.payload.filters.push({ operator: "is", column, value });
    return this;
  }

  order(column: string, options?: { ascending?: boolean }) {
    this.payload.order = { column, ascending: options?.ascending ?? true };
    return this;
  }

  limit(value: number) {
    this.payload.limit = value;
    return this;
  }

  maybeSingle() {
    this.payload.single = true;
    return this;
  }

  then<TResult1 = DataResult<DynamicData>, TResult2 = never>(
    onfulfilled?: ((value: DataResult<DynamicData>) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: unknown) => TResult2 | PromiseLike<TResult2>) | null,
  ): Promise<TResult1 | TResult2> {
    return this.executeRequest(this.payload).then(onfulfilled, onrejected);
  }
}

export interface PlatformDataClient {
  from(table: string): DataQueryBuilder;
  rpc(name: string, args?: Record<string, unknown>): Promise<DataResult<DynamicData>>;
}

export function createApiClient(baseUrl: string, getAccessToken?: () => Promise<string | null>) {
  const baseWithSlash = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
  const normalizedBaseUrl = new URL(
    baseWithSlash,
    typeof globalThis.location === "undefined" ? "http://localhost" : globalThis.location.origin,
  );

  async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = await getAccessToken?.();
    const response = await fetch(new URL(path.replace(/^\//, ""), normalizedBaseUrl), {
      ...init,
      headers: {
        Accept: "application/json",
        ...(init.body ? { "Content-Type": "application/json" } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init.headers,
      },
    }).catch(() => {
      const error = new Error("Cannot connect to the server. Check your connection and try again.");
      publishFeedback(error.message, "error");
      throw error;
    });

    const body = (await response.json().catch(() => {
      const error = new Error("The server returned an unreadable response. Please try again.");
      publishFeedback(error.message, "error");
      throw error;
    })) as T | ApiErrorBody;
    if (!response.ok) {
      const failure = body as Partial<ApiErrorBody> | null;
      const error = new ApiError(response.status, {
        error: {
          code: failure?.error?.code ?? "REQUEST_FAILED",
          requestId: failure?.error?.requestId ?? "",
          message:
            failure?.error?.message ?? "The request could not be completed. Please try again.",
        },
      });
      publishFeedback(error.message, "error");
      throw error;
    }
    const result = body as { error?: { message?: string }; message?: string };
    if (result?.error?.message) publishFeedback(result.error.message, "error");
    else if (typeof result?.message === "string") publishFeedback(result.message);
    return body as T;
  }

  async function dataRequest(payload: QueryPayload): Promise<DataResult<DynamicData>> {
    try {
      return await request<DataResult<DynamicData>>("/v1/data/query", {
        method: "POST",
        body: JSON.stringify(payload),
      });
    } catch (error) {
      return {
        data: null,
        error: { message: error instanceof Error ? error.message : "Request failed" },
      };
    }
  }

  const dataClient: PlatformDataClient = {
    from: (table) => new DataQueryBuilder(table, dataRequest),
    async rpc(name, args = {}) {
      try {
        return await request<DataResult<DynamicData>>(`/v1/data/rpc/${encodeURIComponent(name)}`, {
          method: "POST",
          body: JSON.stringify({ args }),
        });
      } catch (error) {
        return {
          data: null,
          error: { message: error instanceof Error ? error.message : "Request failed" },
        };
      }
    },
  };

  return {
    dataClient,
    requestManualRecovery: (identifier: string) =>
      request<{ message: string }>("/auth/manual-recovery", {
        method: "POST",
        body: JSON.stringify({ identifier }),
      }),
    requestRecovery: (email: string, origin: string) =>
      request<{ message: string }>("/auth/recovery", {
        method: "POST",
        body: JSON.stringify({ email, origin }),
      }),
    resetPassword: (token: string, password: string) =>
      request<{ message: string }>("/auth/reset-password", {
        method: "POST",
        body: JSON.stringify({ token, password }),
      }),
    health: () => request<HealthResponse>("/health"),
    renewSession: () => request<{ session: PlatformSession }>("/v1/session", { method: "POST" }),
    me: () => request<{ user: AuthenticatedUser }>("/v1/me"),
    authenticate: (identifier: string, password: string) =>
      request<{ session: PlatformSession }>("/auth/sign-in", {
        method: "POST",
        body: JSON.stringify({ identifier, password }),
      }),
    register: (input: {
      identifier: string;
      password: string;
      displayName: string;
      username: string;
    }) =>
      request<{ session: PlatformSession }>("/auth/sign-up", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    updateContact: (input: { currentPassword: string; contact: string }) =>
      request<{ session: PlatformSession }>("/v1/account/contact", {
        method: "PUT",
        body: JSON.stringify(input),
      }),
    updatePassword: (input: { currentPassword: string; newPassword: string }) =>
      request<{ session: PlatformSession }>("/v1/account/password", {
        method: "PUT",
        body: JSON.stringify(input),
      }),
  };
}

export { submitOrderRequest } from "./order-request";
